import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import apiClient from "../lib/api";
import PickCard from "../components/PickCard";
import { MagnifyingGlass, Funnel, X, Sparkle } from "@phosphor-icons/react";
import AdminSection from "../components/admin/AdminSection";

const SPORTS = [
  { v: "", l: "Todos" },
  { v: "football", l: "Fútbol" },
  { v: "nba", l: "NBA" },
  { v: "mlb", l: "MLB" },
];

const STATUSES = [
  { v: "", l: "Activos" },
  { v: "pending", l: "Pendientes" },
  { v: "live", l: "En Vivo" },
  { v: "won", l: "Ganadas" },
  { v: "lost", l: "Perdidas" },
  { v: "void", l: "Anuladas" },
];

export default function Picks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valueOnly, setValueOnly] = useState(searchParams.get("value") === "1");
  const [q, setQ] = useState(searchParams.get("q") || "");

  const sport = searchParams.get("sport") || "";
  const status = searchParams.get("status") || "";
  const minConfidence = searchParams.get("minConf") || "";

  const updateParam = (key, val) => {
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const clearAll = () => {
    setSearchParams({});
    setQ("");
    setValueOnly(false);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = {};
        if (sport) params.sport = sport;
        if (status) params.status = status;
        if (minConfidence) params.min_confidence = minConfidence;
        if (valueOnly) params.value_only = true;
        if (q) params.q = q;
        const res = await apiClient.get("/picks", { params });
        setPicks(res.data.picks);
      } catch (err) {
        console.error("[Picks] fetch failed:", err?.response?.data || err.message);
        setPicks([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [sport, status, minConfidence, valueOnly, q]);

  const activeFiltersCount = [sport, status, minConfidence, valueOnly, q].filter(Boolean).length;

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-8 pb-16">
      {/* Header with accent bar */}
      <div className="mb-8 relative">
        <div className="absolute -top-4 left-0 w-20 h-px" style={{ background: "linear-gradient(90deg, #D4AF37, transparent)" }} />
        <div className="text-[11px] tracking-[0.25em] uppercase font-black mb-2" style={{ color: "#D4AF37" }}>Catálogo</div>
        <h1 className="text-display font-black text-3xl lg:text-5xl tracking-tighter leading-none text-white">Jugadas</h1>
        <p className="text-zinc-500 mt-3 text-sm">Análisis publicados por Ronald, filtrables por deporte, estado y nivel de confianza.</p>
      </div>

      {/* Filters */}
      <AdminSection icon={Funnel} title="Filtros" subtitle="Busca por deporte, estado y confianza" color="sky" className="mb-8">
        {/* On mobile: single scrollable row (prevents overflow). On desktop: wraps normally */}
        <div
          className="flex items-center gap-3 overflow-x-auto lg:flex-wrap"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch", paddingBottom: 2 }}
        >
          {/* Search */}
          <div className="relative flex-1 min-w-[120px] flex-shrink-0">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid="picks-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar equipo, liga..."
              className="input-field pl-9"
            />
          </div>

          {/* Sport */}
          <div className="flex gap-1 p-1 bg-obsidian-800 border border-white/[0.06] rounded-md flex-shrink-0">
            {SPORTS.map((s) => (
              <button
                key={s.v}
                data-testid={`picks-filter-sport-${s.v || "all"}`}
                onClick={() => updateParam("sport", s.v)}
                className={`text-xs font-semibold px-3 py-1.5 rounded transition-colors ${
                  sport === s.v ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>

          {/* Status */}
          <div className="flex gap-1 p-1 bg-obsidian-800 border border-white/[0.06] rounded-md flex-shrink-0">
            {STATUSES.map((s) => (
              <button
                key={s.v}
                data-testid={`picks-filter-status-${s.v || "all"}`}
                onClick={() => updateParam("status", s.v)}
                className={`text-xs font-semibold px-2 py-1.5 rounded transition-colors ${
                  status === s.v ? "bg-yellow-400 text-black" : "text-zinc-400 hover:text-white"
                }`}
              >
                {s.l}
              </button>
            ))}
          </div>

          {/* Alta confianza toggle */}
          <button
            data-testid="picks-filter-value-only"
            onClick={() => setValueOnly(!valueOnly)}
            className={`chip flex-shrink-0 ${valueOnly ? "chip-confidence" : "chip-muted"} hover:border-emerald-400/40 transition-colors`}
          >
            {valueOnly ? "✓ " : ""}Alta confianza
          </button>

          {/* Confidence */}
          <select
            data-testid="picks-filter-min-confidence"
            value={minConfidence}
            onChange={(e) => updateParam("minConf", e.target.value)}
            className="input-field max-w-[140px] text-xs flex-shrink-0"
          >
            <option value="">Cualquier confianza</option>
            <option value="50">Confianza ≥ 50</option>
            <option value="70">Confianza ≥ 70</option>
            <option value="85">Confianza ≥ 85</option>
          </select>

          {activeFiltersCount > 0 && (
            <button onClick={clearAll} className="btn-ghost text-xs flex-shrink-0" data-testid="picks-clear-filters">
              <X className="w-3 h-3" /> Limpiar ({activeFiltersCount})
            </button>
          )}
        </div>
      </AdminSection>

      {/* Results */}
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="rounded-2xl h-64 animate-pulse" style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.06)" }} />
          ))}
        </div>
      ) : picks.length === 0 ? (
        <div className="rounded-2xl p-16 text-center" style={{ background: "#16161A", border: "1px solid rgba(255,255,255,0.07)" }}>
          <div className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.2)" }}>
            <Funnel className="w-6 h-6" style={{ color: "#D4AF37" }} />
          </div>
          <div className="font-bold text-lg text-white mb-2">Aún no hay jugadas publicadas</div>
          <div className="text-zinc-500 text-sm">Las jugadas aparecerán aquí a medida que se registren.</div>
          {(sport || status || valueOnly || q || minConfidence) && (
            <button onClick={clearAll} className="btn-secondary mt-5 text-sm">Limpiar filtros</button>
          )}
        </div>
      ) : (
        <AdminSection icon={Sparkle} title="Resultados" subtitle="Jugadas disponibles" color="amber"
          action={<div className="text-xs uppercase tracking-wider font-bold flex items-center gap-2 flex-shrink-0" style={{ color: "#8E8E99" }} data-testid="picks-count">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#D4AF37" }} />
            {picks.length} {picks.length === 1 ? "jugada" : "jugadas"}
          </div>}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...picks]
              // Highlight Pick VIP available for purchase by sorting them first
              .sort((a, b) => {
                const aVip = a.is_paid_pick && a.locked && a.pick_price ? 1 : 0;
                const bVip = b.is_paid_pick && b.locked && b.pick_price ? 1 : 0;
                return bVip - aVip;
              })
              .map((p, idx) => (
                <div key={p.id} className="fade-in-up" style={{ animationDelay: `${idx * 0.05}s` }}>
                  <PickCard pick={p} />
                </div>
              ))}
          </div>
        </AdminSection>
      )}
    </div>
  );
}
